import React, {
  useMemo,
  useEffect,
  useState,
  useCallback,
} from 'react';
import { useQuery } from 'react-query';
import { css } from '@emotion/core';
import { Interpreter } from 'xstate';
import { useService } from '@xstate/react';

import Modal from '~/theseus/components/Modal';
import SectionToggle from '~/theseus/components/SectionToggle';
import Spinner from '~/theseus/components/Spinner';

import {
  TosContext,
  TosSchema,
  TosEvent,
} from '../machine/tosMachine';

import {
  ModalContent,
  FacebookLink,
  PageName,
  InstructionsContent,
  AwaitingApprovalContent,
} from './core';
import { useAuthSelector } from '~/theseus/containers/Auth';
import { usePageQuery, usePagesQuery } from '~/theseus/hooks/queries';

const appEl = document.getElementById('lead-ad-terms-of-service');

const MODAL_CONTENT_STYLES = {
  height: 580,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
};

function Instructions(props: { pageName: string; onLinkClick: () => void; }) {
  const { pageName, onLinkClick } = props;

  return (
    <ModalContent>
      <h2>Please Accept Facebook&apos;s Lead Ads Terms of Service</h2>
      <InstructionsContent>
        <ol>
          <li>
            Click the &quot;Review and Accept&quot; button above to see Facebook&apos;s Lead
            Ads Terms of Service
          </li>
          <li>
            Select&nbsp;
            <PageName>{ pageName }</PageName>
            &nbsp;from the dropdown
          </li>
          <li>Click &quot;Accept&quot;</li>
          <li>Return to this page</li>
        </ol>
      </InstructionsContent>
      <FacebookLink
        as="a"
        href="https://www.facebook.com/ads/leadgen/tos"
        target="_blank"
        size="lg"
        onClick={onLinkClick}
      >
        Review and Accept Lead Ads Terms
      </FacebookLink>
    </ModalContent>
  );
}

function AwaitingApproval(props: { pageName: string }) {
  const { pageName } = props;

  const [showingMessage, setShowingMessage] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => setShowingMessage(true), 10000);

    return () => clearTimeout(timeout);
  }, []);

  return (
    <ModalContent>
      <h2>Awaiting confirmation from Facebook...</h2>
      <Spinner radius={60} css={css`margin: 2em;`} data-testid="spinner" />
      <SectionToggle enabledOn={showingMessage}>
        <AwaitingApprovalContent>
          <h3>
            Lead Ads Terms of Service not accepted for&nbsp;
            <PageName>{ pageName }</PageName>
          </h3>
          <p>
            Make sure that you select and accept the terms for&nbsp;
            <PageName>{ pageName }</PageName>
            &nbsp;on Facebook.
          </p>
        </AwaitingApprovalContent>
      </SectionToggle>
    </ModalContent>
  );
}

interface Props {
  termsRef: Interpreter<TosContext, TosSchema, TosEvent>
}

export default function LeadAdTermsServiceModal(props: Props) {
  const { termsRef } = props;

  const [pageId] = usePageQuery();
  const accessToken = useAuthSelector(state => state.context.authToken);

  const { data: pages } = usePagesQuery();

  const pageName = useMemo(() => pages?.find(({ id }) => id === pageId)?.name, [pages, pageId]);

  const [state, send] = useService(termsRef);

  const beginTOSAcceptance = useCallback(() => {
    send('TOS.beginPolling');
  }, [send]);

  const closeModal = useCallback(() => {
    send('MODAL.close');
  }, [send]);

  const modalContent = useMemo(() => {
    if (state.matches('open.instructions')) {
      return <Instructions pageName={pageName as string} onLinkClick={beginTOSAcceptance} />;
    }

    if (state.matches('open.awaitingFBResponse')) {
      return <AwaitingApproval pageName={pageName as string} />;
    }

    return <div />;
  }, [state, beginTOSAcceptance, pageName]);

  return (
    <Modal
      isOpen={state.matches('open')}
      appElement={appEl}
      contentStyle={MODAL_CONTENT_STYLES}
      onClose={closeModal}
    >
      { modalContent }
    </Modal>
  );
}
